import React from "react";
import { Trash2, History } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetBody,
} from "./ui/sheet";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { shortAddr, timeAgo } from "../lib/utils";
import { cn } from "../lib/utils";
import { chainLabel } from "../lib/chainValidation";

const CHAIN_COLORS = {
  solana: "bg-[#9945FF]",
  robinhood: "bg-[#00C805]",
  eth: "bg-[#627EEA]",
  base: "bg-[#0052FF]",
  bsc: "bg-[#F3BA2F]",
  tron: "bg-[#FF0013]",
  ton: "bg-[#0088CC]",
  hyperliquid: "bg-[#00E5BE]",
};

function ChainChip({ chain }) {
  const dot = CHAIN_COLORS[chain] || "bg-[hsl(var(--muted))]";
  return (
    <span
      data-testid="recent-chain-chip"
      className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev-1))] text-[hsl(var(--muted))]"
    >
      <span className={cn("h-1.5 w-1.5 rounded-full inline-block", dot)} />
      {chainLabel(chain || "solana")}
    </span>
  );
}

function labelVariant(label) {
  if (label === "Good") return "success";
  if (label === "Warning") return "warning";
  if (label === "Danger") return "danger";
  return "muted";
}

export function RecentScansDrawer({ open, onOpenChange, items, onPick, onClear }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <div className="flex items-center justify-between gap-2">
            <SheetTitle className="flex items-center gap-2">
              <History size={16} /> Recent Scans
            </SheetTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClear}
              data-testid="recent-scans-clear-button"
              disabled={!items?.length}
              className="text-[hsl(var(--muted))]"
            >
              <Trash2 size={14} /> Clear
            </Button>
          </div>
          <p className="mt-1 text-xs text-[hsl(var(--muted))]">
            Up to 20 most recent scans, stored locally in your browser.
          </p>
        </SheetHeader>
        <SheetBody>
          {items?.length ? (
            <ul className="space-y-1.5">
              {items.map((it) => (
                <li key={`${it.chain || "solana"}-${it.mint}`}>
                  <button
                    type="button"
                    onClick={() => onPick?.(it.mint, it.chain || "solana")}
                    className={cn(
                      "w-full text-left rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev-2))] hover:bg-[hsl(var(--bg-elev-1))] hover:border-[hsl(var(--primary)/0.4)] transition-colors px-3 py-2.5"
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        {it.logo ? (
                          <img
                            src={it.logo}
                            alt=""
                            className="h-7 w-7 rounded-full border border-[hsl(var(--border))]"
                            loading="lazy"
                          />
                        ) : (
                          <div className="h-7 w-7 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--bg-elev-1))] grid place-items-center text-[10px] font-mono text-[hsl(var(--muted))]">
                            {(it.symbol || "?").slice(0, 2).toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">
                            {it.name || shortAddr(it.mint, 6, 6)}{" "}
                            <span className="text-[hsl(var(--muted))] font-mono text-[11px] uppercase">
                              {it.symbol ? `· ${it.symbol}` : ""}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <ChainChip chain={it.chain || "solana"} />
                            <span className="text-[11px] font-mono text-[hsl(var(--muted))] truncate">
                              {shortAddr(it.mint, 6, 6)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <div className="flex items-center gap-1">
                          {typeof it.score === "number" ? (
                            <span className="font-mono text-sm tabular-nums">
                              {it.score}
                            </span>
                          ) : null}
                          <Badge variant={labelVariant(it.label)}>
                            {it.label || "?"}
                          </Badge>
                        </div>
                        <span className="text-[10px] text-[hsl(var(--muted))]">
                          {timeAgo(Math.floor((it.ts || 0) / 1000))}
                        </span>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="py-12 text-center text-sm text-[hsl(var(--muted))]">
              No scans yet. Select a chain, paste an address, and hit Scan.
            </div>
          )}
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}
